/**
 * <bb-collection-load-more data-section-id="..."> — "Ver más productos" for the collection grid.
 * The search results page reuses it with data-bb-more-grid="<css selector of its product grid>".
 *
 * Progressive enhancement: the [data-bb-more-link] anchor is a real link to the next page, so without JS
 * it simply navigates. With JS the click is intercepted and the next page is fetched through the Section
 * Rendering API (`?page=N&section_id=<id>`). The fetched product cells are appended to the current grid and
 * the progress text, bar, button and fallback pager are refreshed from the same response. Filters are
 * already part of the next-page URL, so they are preserved automatically.
 *
 * Appended cards need no manual init: <bb-quick-view> and Horizon's quick-add / product-form components
 * are custom elements, so they upgrade as soon as the nodes are connected to the document.
 *
 * Note: this theme scrolls inside `.page-wrapper`, never the window, so nothing here touches window scroll.
 */
class BBCollectionLoadMore extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-bb-more-link]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    // Let modified clicks (new tab, etc.) keep the native link behaviour
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (this.#loading) return;
    // detail === 0 means the click came from the keyboard (Enter on the link)
    this.#load(link, event.detail === 0);
  };

  /** @param {HTMLAnchorElement} link @param {boolean} fromKeyboard */
  async #load(link, fromKeyboard) {
    const grid = this.#grid();
    const sectionId = this.dataset.sectionId;
    if (!grid || !sectionId) {
      window.location.href = link.href;
      return;
    }

    this.#loading = true;
    const label = link.textContent;
    link.setAttribute('aria-busy', 'true');
    link.textContent = 'Cargando…';
    this.setAttribute('aria-busy', 'true');

    try {
      const pageUrl = new URL(link.href, window.location.href);
      const fetchUrl = new URL(pageUrl.href);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl.href, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      // Collection page: the default grid/cell classes. Other pages (search) point to their own grid with
      // data-bb-more-grid="<selector>"; every direct child of that grid is then treated as a cell.
      const customGrid = this.dataset.bbMoreGrid;
      const nextGrid = doc.querySelector(customGrid || '.bb-col__grid:not(.bb-col__grid--demo)');
      const cells = nextGrid ? Array.from(nextGrid.querySelectorAll(customGrid ? ':scope > *' : ':scope > .bb-col__cell')) : [];
      if (cells.length === 0) throw new Error('Load more response has no products');

      // importNode into the live document, then append: custom elements upgrade on connection
      const fragment = document.createDocumentFragment();
      for (const cell of cells) fragment.append(document.importNode(cell, true));
      const firstNewCell = fragment.firstElementChild;
      grid.append(fragment);

      this.#sync(doc.querySelector('bb-collection-load-more'));
      window.history.replaceState(window.history.state, '', pageUrl.href);
      this.#focusFirst(firstNewCell, fromKeyboard);
    } catch (error) {
      console.error('[bb-collection-load-more]', error);
      // Restore the button, then fall back to a regular page navigation
      link.removeAttribute('aria-busy');
      link.textContent = label;
      window.location.href = link.href;
    } finally {
      this.removeAttribute('aria-busy');
      this.#loading = false;
    }
  }

  /** @returns {HTMLElement | null} */
  #grid() {
    const customGrid = this.dataset.bbMoreGrid;
    if (customGrid) return document.querySelector(customGrid);
    const main = this.closest('.bb-col__main');
    return main ? main.querySelector('.bb-col__grid:not(.bb-col__grid--demo)') : null;
  }

  /**
   * Refreshes progress text, bar, button and pager from the fetched page. Pieces are updated in place
   * (not the whole block) so the polite live region keeps announcing the new count.
   * @param {Element | null} fresh
   */
  #sync(fresh) {
    const swap = (selector, mode) => {
      const current = this.querySelector(selector);
      const next = fresh ? fresh.querySelector(selector) : null;
      if (!current) return;
      if (mode === 'width') {
        const width = next instanceof HTMLElement ? next.style.width : '';
        if (width) current.style.width = width;
        return;
      }
      current.innerHTML = next ? next.innerHTML : '';
    };

    swap('[data-bb-more-text]', 'html');
    swap('[data-bb-more-fill]', 'width');
    swap('[data-bb-more-action]', 'html');
    swap('[data-bb-more-pager]', 'html');
  }

  /**
   * Moves focus to the first new product link. Pointer users keep their scroll position;
   * keyboard users get the minimum scroll needed to see the focused link.
   * @param {Element | null} cell @param {boolean} fromKeyboard
   */
  #focusFirst(cell, fromKeyboard) {
    const target = cell ? cell.querySelector('.bb-card__name a') : null;
    if (!(target instanceof HTMLElement)) return;
    target.focus({ preventScroll: true });
    if (fromKeyboard) target.scrollIntoView({ block: 'nearest' });
  }
}

if (!customElements.get('bb-collection-load-more')) {
  customElements.define('bb-collection-load-more', BBCollectionLoadMore);
}
